import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  inject
} from '@angular/core';
import {
  LegService,
  NotificationService,
  ProductsConfigService,
  RfqDetails,
  RfqService,
  StrategyDetailService
} from '@approot/services';
import { SubscriptionSink, clone, isFeatureEnabled } from '@approot/utilities';
import {
  ColumnApi,
  GridApi,
  GridReadyEvent,
  RowClickedEvent
} from 'ag-grid-community';
import NodeCache from 'node-cache';
import { forkJoin, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { ActionStatusMap } from '../provider-quote-prices/provider-quote-types';
import { GridRow, ProviderQuoteEvent } from '../provider-quote.model';
import { ProviderQuoteService } from '../provider-quote.service';
import { columnDefs, defaultColDef } from './ag-helpers';

@Component({
  selector: 'app-provider-rfqs',
  templateUrl: './provider-rfqs.component.html',
  styleUrls: ['./provider-rfqs.component.scss'],
  providers: [LegService] // each tab has its own leg service
})
export class ProviderRfqsComponent implements OnDestroy, OnInit {
  @Output() public tradesLoaded = new EventEmitter<Record<string, any>[]>();
  public columnDefs = columnDefs;
  public defaultColDef = defaultColDef;
  public selected: GridRow[] = [];
  public modalOpened = false;

  private gridApi: GridApi;
  private columnApi: ColumnApi;
  private readonly subsink = new SubscriptionSink();
  private lastData: GridRow[] = [];
  private eventsSubscribed = false;
  private strategyDetailService = inject(StrategyDetailService);
  private cache = new NodeCache({ stdTTL: 60 * 30 });
  private rfqDetailsCache = new NodeCache({ stdTTL: 60 * 30 });
  private notificationService = inject(NotificationService);

  constructor(
    private rfqService: RfqService,
    private providerQuoteService: ProviderQuoteService,
    private legService: LegService,
    private productsConfigService: ProductsConfigService
  ) {
    this.legService.productSpecifications = clone(
      this.productsConfigService.productInfo.productSpecifications
    );
  }

  public ngOnInit(): void {
    if (isFeatureEnabled('enableDesktopNotifications', false)) {
      this.notificationService.requestPermission().subscribe(permission => {
        if (permission !== 'granted') {
          console.warn('Notification permission denied');
        }
      });
    }
  }

  public ngOnDestroy() {
    this.rfqDetailsCache.close();
    this.cache.close();
    this.subsink.unsubscribe();
  }

  public onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
    this.gridApi.setRowData([]);
    if (isFeatureEnabled('localDev', false)) {
      import('./example').then(data => {
        this.upsert(data.default as ProviderQuoteEvent[]);
      });
    }

    this.rfqService.openQuotes().subscribe({
      next: response => {
        const pqes: ProviderQuoteEvent[] = response.result.map(quote => ({
          quote
        }));
        if (pqes.length) {
          this.upsert(pqes, () => {
            this.updateRfqDetails(pqes);
            this.subscribeToEvents();
          });
        } else {
          this.subscribeToEvents();
        }
      },
      error: () => this.subscribeToEvents()
    });
  }

  private upsert(quoteEvents: ProviderQuoteEvent[], complete?: Function) {
    if (!quoteEvents?.length) {
      return;
    }
    const obsArr = quoteEvents.map(quoteEvent => {
      const requestId = quoteEvent.quote.request.requestId;

      return of(this.cache.get(requestId)).pipe(
        switchMap(cachedHydraResponse => {
          if (cachedHydraResponse) {
            return of(cachedHydraResponse);
          } else {
            return this.providerQuoteService.extractHydraProductsFromMedusaProviderQuoteEvent(
              quoteEvent
            );
          }
        }),
        tap(hydraResponse => {
          this.cache.set(requestId, hydraResponse);
        }),
        switchMap(hydraResponse => {
          return of({ hydraResponse, quoteEvent, requestId });
        })
      );
    });

    // the "complete()" function (probably "subscribeToEvents()") should only
    // run after all incoming quotes are processed to avoid a race. This is only
    // relevant for the initial loading of historical quotes loaded via "openQuotes()"
    forkJoin(obsArr).subscribe(results => {
      results.forEach(({ hydraResponse, quoteEvent, requestId }) => {
        const formatted = this.formatData(quoteEvent, hydraResponse);
        let idx = this.lastData.findIndex(r => r.requestId === requestId);
        if (idx !== -1) {
          // replace existing row
          this.lastData.splice(idx, 1, formatted);
        } else {
          this.lastData.push(formatted);
        }
        this.lastData.sort((a, b) => b.requestId - a.requestId);
        const ignore = ['Cancelled'].map(item => item.toLowerCase());
        this.lastData = this.lastData.filter(row => {
          return !ignore.includes(row.action?.toLowerCase());
        });
        this.gridApi.setRowData(this.lastData);

        // new index after sorting
        idx = this.lastData.findIndex(r => r.requestId === requestId);
        this.flashRow(idx, formatted.requestId);
      });
      complete?.();
    });
  }

  private updateRfqDetails(quoteEvents: ProviderQuoteEvent[]) {
    quoteEvents.forEach(quoteEvent => this.updateRfqDetail(quoteEvent));
  }

  private updateRfqDetail(quoteEvent: ProviderQuoteEvent) {
    const refresh = (rfqDetails: RfqDetails) => {
      let idx = this.lastData.findIndex(r => r.requestId === requestId);
      if (idx !== -1) {
        this.lastData[idx].rfqDetails = rfqDetails;
        const tab = this.providerQuoteService.tabs.find(
          r => r.requestId === requestId
        );
        if (tab) {
          /* at times RfqDetails that is returned is an empty object,
          so the object in the tab needs to be updated. */
          tab.rfqDetails = rfqDetails;
        }
        this.gridApi.refreshCells();
      }
    };

    const requestId = quoteEvent.quote.request.requestId;
    const cached = this.rfqDetailsCache.get(requestId) as RfqDetails;
    if (cached) {
      refresh(cached);
      return;
    }
    this.rfqService.getRfqDetails(requestId).subscribe({
      next: resp => {
        if (resp) {
          this.rfqDetailsCache.set(requestId, resp);
          refresh(resp);
        }
      }
    });
  }

  public rowClicked(event: RowClickedEvent) {
    if (this.strategyDetailService.tradingMode) {
      this.modalOpened = true;
      return;
    }
    const data: GridRow = event.data;

    if (this.providerQuoteService.quoteExists(data.quoteEvent)) {
      console.warn(
        'Tab for that quote is already open',
        data.quoteEvent.quote.request.requestId
      );
      return;
    }

    this.providerQuoteService.addNewTab(
      data.quoteEvent,
      data.legs,
      data.rfqDetails
    );
  }

  public loadTrades() {
    this.tradesLoaded.emit(this.selected[0].legs);
  }

  public selectionChanged() {
    this.selected = this.gridApi.getSelectedRows();
  }

  public get buttonsEnabled(): boolean {
    return this.selected?.length > 0;
  }

  public firstDataRendered() {
    this.resizeColumns();
  }

  /*<PRIVATE>*********************************/

  private subscribeToEvents() {
    // prevent double subscription
    if (this.eventsSubscribed) {
      return;
    }
    this.eventsSubscribed = true;
    this.subsink.sink = this.rfqService.events$
      .pipe(
        map(evt => evt.filter(row => row['@type'] === 'ProviderQuoteEvent'))
      )
      .subscribe((quoteEvent: ProviderQuoteEvent[]) => {
        this.upsert(quoteEvent);
        this.updateRfqDetails(quoteEvent);
      });
  }

  private resizeColumns(skipHeader = false) {
    const colIds = this.columnApi
      .getAllColumns()
      .map(column => column.getColId());

    this.columnApi.autoSizeColumns(colIds, skipHeader);
  }

  private formatData(
    quoteEvent: ProviderQuoteEvent,
    legs: Record<string, any>[]
  ): GridRow {
    const { quote, action } = quoteEvent;
    const { premiumDate, owner, quoteStatus, request, tradeDirection } = quote;
    const {
      requestId,
      requester,
      strategy,
      hedge,
      premiumCurrency,
      quotePair
    } = request;

    return {
      quoteStatus: (ActionStatusMap[quoteStatus] ?? quoteStatus) as any,
      action: (ActionStatusMap[action] ?? action) as any,
      requestId,
      requester,
      owner,
      strategy,
      cross: `${quotePair.base}${quotePair.term}`,
      hedge,
      premiumCurrency,
      tradeDirection,
      premiumDate,
      quoteEvent,
      legs
    };
  }

  private flashRow(i: number, requestId: number) {
    if (i === -1) {
      i = 0;
    }

    var row = this.gridApi.getDisplayedRowAtIndex(i);
    var rowData = row?.data as GridRow;

    if (row && rowData?.requestId === requestId) {
      this.gridApi.flashCells({ rowNodes: [row, row] });
    }
  }
}
